/**
 * This is based on the Mutation component in React Apollo.
 * https://github.com/apollographql/react-apollo/blob/v2.5.7/src/Mutation.tsx
 *
 * However, we've dropped props we weren't using, the Apollo Context support,
 * replaced ApolloClient with ApolloGqlClient, and other related changes.
 */
import * as React from "react";

import {graphQLDocumentNodeParser} from "@khanacademy/wonder-blocks-data";
import type {
    ApolloGqlClient,
    ApolloMutationOptions,
    ApolloResult,
    AsyncApolloMutationResult,
} from "@repo/data-gql-apollo";
import {documentNode, type GqlOperation} from "@repo/data-gql-core";
import {Errors, KAError} from "@repo/logging";

import type ApolloGqlError from "../apollo-gql-error.ts";

type Props<TData, TVariables> = {
    // @ts-expect-error - TS2344 - Type 'TVariables' does not satisfy the constraint 'object'.
    mutation: GqlOperation<TData, TVariables>;
    children: (
        mutateFn: (
            // @ts-expect-error - TS2344
            options?: ApolloMutationOptions<TData, TVariables>,
        ) => Promise<ApolloResult<TData> | null | undefined>,
        result: AsyncApolloMutationResult<TData>,
    ) => React.ReactNode;
    onCompleted?: (data?: TData) => void;
    onError?: (error: ApolloGqlError) => void;
    client: ApolloGqlClient;
};

type State<TData> = {
    called: boolean;
    error?: ApolloGqlError;
    data?: TData;
    loading: boolean;
    /**
     * We need to clear our state when the client prop changes so we have to
     * track the client prop in state in order to do that in a safe manner.
     */
    client: ApolloGqlClient;
};

const initialState = {
    loading: false,
    called: false,
    error: undefined,
    data: undefined,
} as const;

export default class ApolloMutationCore<
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
> extends React.Component<Props<TData, TVariables>, State<TData>> {
    _mostRecentMutationId: number;
    _hasMounted: boolean;

    constructor(props: Props<TData, TVariables>) {
        super(props);
        this._hasMounted = false;
        this._verifyDocumentIsMutation(props.mutation);
        this._mostRecentMutationId = 0;
        this.state = {
            ...initialState,
            client: props.client,
        };
    }

    // We want to make sure that we reset our state when the client prop
    // changes.
    // eslint-disable-next-line no-restricted-syntax
    static getDerivedStateFromProps<
        TData extends Record<any, any>,
        TVariables extends Record<any, any>,
    >(
        props: Props<TData, TVariables>,
        state: State<TData>,
    ): null | State<TData> {
        if (props.client !== state.client) {
            return {
                ...initialState,
                client: props.client,
            };
        }
        return null;
    }

    componentDidMount() {
        this._hasMounted = true;
    }

    componentDidUpdate(prevProps: Props<TData, TVariables>) {
        if (prevProps.mutation !== this.props.mutation) {
            this._verifyDocumentIsMutation(this.props.mutation);
        }
    }

    componentWillUnmount() {
        this._hasMounted = false;
    }

    // @ts-expect-error - TS2322 - Type '(options?: ApolloMutationOptions<TData, TVariables> | undefined) => Promise<void | ApolloResult<TData>>' is not assignable to type '(options?: ApolloMutationOptions<TData, TVariables> | undefined) => Promise<ApolloResult<TData> | null | undefined>'.
    _runMutation: (
        options?: ApolloMutationOptions<TData, TVariables>,
    ) => Promise<ApolloResult<TData> | null | undefined> = (
        options = {} as any,
    ) => {
        const {mutation, client} = this.props;
        this._onMutationStart();
        const mutationId = this._generateNewMutationId();

        return client
            .mutate<TData, TVariables>(mutation, options)
            .then((response) => {
                this._onMutationCompleted(response, mutationId);
                return response;
            })
            .catch((e: ApolloGqlError) => {
                this._onMutationError(e, mutationId);
                if (!this.props.onError) {
                    throw e;
                }
            });
    };

    _onMutationStart: () => void = () => {
        if (!this.state.loading && this._hasMounted) {
            this.setState({
                loading: true,
                error: undefined,
                data: undefined,
                called: true,
            });
        }
    };

    _onMutationCompleted: (
        response: ApolloResult<TData>,
        mutationId: number,
    ) => void = (response, mutationId) => {
        const {onCompleted} = this.props;

        const {data} = response;
        const callOncomplete = () => (onCompleted ? onCompleted(data) : null);

        if (this._hasMounted && this._isMostRecentMutation(mutationId)) {
            this.setState({loading: false, data}, callOncomplete);
        } else {
            callOncomplete();
        }
    };

    _onMutationError: (error: ApolloGqlError, mutationId: number) => void = (
        error,
        mutationId,
    ) => {
        const {onError} = this.props;
        const callOnError = () => (onError ? onError(error) : null);

        if (this._hasMounted && this._isMostRecentMutation(mutationId)) {
            this.setState({loading: false, error}, callOnError);
        } else {
            callOnError();
        }
    };

    _generateNewMutationId: () => number = () => {
        this._mostRecentMutationId = this._mostRecentMutationId + 1;
        return this._mostRecentMutationId;
    };

    _isMostRecentMutation: (mutationId: number) => boolean = (mutationId) => {
        return this._mostRecentMutationId === mutationId;
    };

    _verifyDocumentIsMutation: (
        mutation: GqlOperation<TData, TVariables>,
    ) => void = (mutation) => {
        const operation = graphQLDocumentNodeParser(
            documentNode(this.props.mutation),
        );
        if (operation.type === "query") {
            throw new KAError(
                "The <ApolloMutation /> component requires a graphql mutation, but got a query.",
                Errors.InvalidInput,
            );
        }
    };

    render(): React.ReactNode {
        const {children} = this.props;
        const {loading, data, error, called} = this.state;

        const result = {
            called,
            loading,
            data,
            error,
        } as const;

        return children(this._runMutation, result);
    }
}
